import { useEffect, useState } from "react";
import dayjs, { Dayjs } from "dayjs";
import {
  Autocomplete,
  Box,
  TextField,
  Skeleton,
  Typography,
  FormControl,
  Select,
  MenuItem,
  SelectChangeEvent,
  InputLabel,
  ListItemText,
  Button,
  IconButton,
  Popover,
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";

import ShirtModelService from "../../services/shirts/shirtModelService";
import {
  Brand,
  Competition,
  Condition,
  Player,
  Season,
  ShirtType,
  Signature,
  Team,
  Variant,
} from "../../services/shirts/types";
import { Dictionary } from "../../types/types";

import FormatService from "../../services/formatService";
import { Shirt } from "../../entities/Shirt";
import { Actions, Subjects } from "../../permission/constants";

import PremiumWrapperButton from "../PremiumWrapperButton/PremiumWrapperButton";
import ItemPropertyForm from "../ItemPropertyForm/ItemPropertyForm";

import CustomSwitch from "../CustomSwitch/CustomSwitch";
import { HelpOutline } from "@mui/icons-material";
import { updateCustomPrice } from "../../services/api/shirtService";
import DialogService from "../Dialog/dialogService";

interface EditShirtFormProps {
  setShirtData: Function;
  setProperties?: Function;
  shirt: Shirt;
  errors?: Dictionary<boolean>;
}

const tfMainStyle = {
  "& .MuiOutlinedInput-notchedOutline": {
    borderWidth: "0px",
  },
};

const tfLabelProps = {
  sx: {
    color: "#6f7d95",
    fontSize: "1rem",
    fontWeight: 500,
  },
};

const tfInputStyle = {
  backgroundColor: "#f4f6f9",
  borderRadius: "3px",
  color: "#6f7d95",
  fontSize: "1rem",
  fontWeight: 500,
  "& .MuiListItemText-root": {
    marginTop: "0px",
    marginBottom: "0px",
  },
};

const subcategoryTitleStyle = { fontSize: "1rem !important" };
const subcategoryTitleErrorStyle = {
  fontSize: "1rem !important",
  color: "red !important",
};

const shirtSizeOptions = ["XS", "S", "M", "L", "XL", "XXL", "XXXL", "XXXXL"];

const EditShirtForm = ({
  setShirtData,
  setProperties,
  shirt,
  errors,
}: EditShirtFormProps) => {
  const [title, setTitle] = useState<string | undefined>(shirt.title);
  const [description, setDescription] = useState<string | undefined>(
    shirt.description
  );
  const [purchaseDate, setPurchaseDate] = useState<Dayjs | null>(
    dayjs(shirt.purchase_date)
  );
  const [purchasePrice, setPurchasePrice] = useState<number | undefined>(
    Number(shirt.purchase_price)
  );
  const [certificate, setCertificate] = useState<boolean>(shirt.certificate);
  const [isPrivate, setIsPrivate] = useState<boolean>(shirt.is_private);
  const [condition, setCondition] = useState<Condition | undefined>(
    shirt.condition
  );
  const [signature, setSignature] = useState<boolean>(
    shirt.signature ? true : false
  );
  const [signatureData, setSignatureData] = useState<string | undefined>(
    shirt.signature ? shirt.signature.description : undefined
  );
  const [player, setPlayer] = useState<Player | undefined>(
    shirt?.model?.player ? shirt?.model?.player : undefined
  );
  const [team, setTeam] = useState<Team | undefined>(shirt.model.team);
  const [competition, setCompetition] = useState<Competition | undefined>(
    shirt.model.competition
  );
  const [variant, setVariant] = useState<Variant | undefined>(
    shirt.model.variant
  );
  const [season, setSeason] = useState<Season | undefined>(shirt.model.season);
  const [number, setNumber] = useState<number | undefined>(shirt.model.number);

  const [brand, setBrand] = useState<Brand | undefined>(shirt.model.brand);
  const [shirtType, setShirtType] = useState<ShirtType | undefined>(
    shirt.model.type
  );
  const [openOffers, setOpenOffers] = useState<boolean>(
    shirt.irresistible_offers
  );
  const [size, setSize] = useState<string>(shirt?.size ?? shirtSizeOptions[0]);

  const [conditionOptions, setConditionOptions] = useState<
    Array<Condition> | undefined
  >(undefined);
  const [brandOptions, setBrandOptions] = useState<Array<Brand> | undefined>(
    undefined
  );
  const [typeOptions, setTypeOptions] = useState<Array<ShirtType> | undefined>(
    undefined
  );
  const [variantOptions, setVariantOptions] = useState<
    Array<Variant> | undefined
  >(undefined);
  const [competitionOptions, setCompetitionOptions] = useState<
    Array<Competition> | undefined
  >(undefined);
  const [seasonOptions, setSeasonOptions] = useState<Array<Season> | undefined>(
    undefined
  );
  const [teamOptions, setTeamOptions] = useState<Array<Team> | undefined>(
    undefined
  );
  const [playerOptions, setPlayerOptions] = useState<Array<Player> | undefined>(
    undefined
  );
  const [numberOptions, setNumberOptions] = useState<Array<number> | undefined>(
    undefined
  );

  const [customPrice, setCustomPrice] = useState<string>();

  // Free text input values for flexible fields
  const [teamInputValue, setTeamInputValue] = useState<string>("");
  const [playerInputValue, setPlayerInputValue] = useState<string>("");
  const [brandInputValue, setBrandInputValue] = useState<string>("");
  const [numberInputValue, setNumberInputValue] = useState<string>("");

  const {
    getBrandByTeamAndSeason,
    getNumbersByPlayerInfo,
    getAllCompetitions,
    getTeamsByCompetitionAndSeason,
    getSeasonsByCompetition,
    getAllPlayersByTeamAndSeasonAndCompetition,
    getAllTypes,
    getAllVariants,
    getAllConditions,
  } = ShirtModelService();
  const { getPlayerName, getUrlLogo, getMoneyFormat } = FormatService();

  const { close: closeDialog } = DialogService();

  // Initialize input values from existing shirt data
  useEffect(() => {
    // Initialize team input - prioritize custom values
    if (shirt?.team_custom) {
      setTeamInputValue(shirt.team_custom);
      setTeam(undefined);
    } else if (shirt?.model?.team) {
      setTeamInputValue(shirt.model.team.name);
    }

    // Initialize brand input - prioritize custom values
    if (shirt?.brand_custom) {
      setBrandInputValue(shirt.brand_custom);
      setBrand(undefined);
    } else if (shirt?.model?.brand) {
      setBrandInputValue(shirt.model.brand.name);
    }

    // Initialize player input - prioritize custom values
    if (shirt?.player_custom) {
      setPlayerInputValue(shirt.player_custom);
      setPlayer(undefined);
    } else if (shirt?.model?.player) {
      setPlayerInputValue(getPlayerName(shirt.model.player));
    }

    // Initialize number input - prioritize custom values
    if (shirt?.custom_number) {
      setNumberInputValue(String(shirt.custom_number));
      setNumber(undefined);
    } else if (shirt?.model?.number) {
      setNumberInputValue(String(shirt.model.number));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shirt]);

  const handleConditionChange = (newId: number) => {
    const foundObject = conditionOptions?.find((obj) => obj.id === newId);
    const foundCondition = foundObject ? foundObject : undefined;
    setCondition(foundCondition);
  };

  const handleTypeChange = (newId: number | undefined) => {
    if (newId) {
      const foundObject = typeOptions?.find((obj) => obj.id === newId);
      const foundType = foundObject ? foundObject : undefined;
      setShirtType(foundType);
    } else {
      setShirtType(undefined);
    }
  };

  const handleVariantChange = (newId: number) => {
    const foundObject = variantOptions?.find((obj) => obj.id === newId);
    const foundVariant = foundObject ? foundObject : undefined;
    setVariant(foundVariant);
  };

  const handleCompetitionChange = (newId: string) => {
    const foundObject = competitionOptions?.find((obj) => obj.id === newId);
    const foundCompetition = foundObject ? foundObject : undefined;
    setCompetition(foundCompetition);
  };

  const handleBrandChange = (newId: number) => {
    const foundObject = brandOptions?.find((obj) => obj.id === newId);
    const foundBrand = foundObject ? foundObject : undefined;
    setBrand(foundBrand);
  };

  const handleSeasonChange = (newId: string) => {
    const foundObject = seasonOptions?.find((obj) => obj.id === newId);
    const foundSeason = foundObject ? foundObject : undefined;
    setSeason(foundSeason);
  };

  const handleTeamChange = (newId: string) => {
    const foundObject = teamOptions?.find((obj) => obj.id === newId);
    const foundTeam = foundObject ? foundObject : undefined;
    setTeam(foundTeam);
  };

  const handlePlayerChange = (newId: string) => {
    const foundObject = playerOptions?.find((obj) => obj.id === newId);
    const foundPlayer = foundObject ? foundObject : undefined;
    setPlayer(foundPlayer);
  };

  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const id = open ? "help-popover" : undefined;

  useEffect(() => {
    getAllConditions().then((conditions) => {
      setConditionOptions(conditions);
    });
    getAllTypes().then((types) => {
      setTypeOptions(types);
    });
    getAllVariants().then((variants) => {
      setVariantOptions(variants);
    });
    getAllCompetitions().then((comp) => {
      if (comp) {
        const sortedComp = comp.sort((a, b) => {
          if (a.name === "National Teams") return -1;
          if (b.name === "National Teams") return 1;
          return a.name.localeCompare(b.name);
        });
        setCompetitionOptions(sortedComp);
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setShirtData("title", title);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [title]);

  useEffect(() => {
    setShirtData("description", description);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [description]);

  useEffect(() => {
    setShirtData("purchase_date", purchaseDate?.format("YYYY/MM/DD"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchaseDate]);

  useEffect(() => {
    setShirtData("purchase_price", purchasePrice);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [purchasePrice]);

  useEffect(() => {
    setShirtData("certificate", certificate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [certificate]);

  useEffect(() => {
    setShirtData("is_private", isPrivate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPrivate]);

  useEffect(() => {
    setShirtData("condition_id", condition?.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [condition]);

  useEffect(() => {
    if (size) {
      setShirtData("size", size);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [size]);

  useEffect(() => {
    const signatureInfo: Signature | undefined = signature
      ? {
          item_id: shirt.id,
          description: "",
        }
      : undefined;
    setShirtData("signature", signatureInfo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  useEffect(() => {
    const signatureInfo: Signature = {
      item_id: shirt.id,
      description: signatureData ? signatureData : "",
    };
    setShirtData("signature", signatureInfo);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signatureData]);

  useEffect(() => {
    setShirtData("player_id", player?.id);
    setShirtData(
      "player_custom",
      player ? undefined : playerInputValue || undefined
    );

    // Reset dependent fields when switching to custom player
    if (!player && playerInputValue) {
      setNumberOptions(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player, playerInputValue]);

  useEffect(() => {
    setShirtData("team_id", team?.id);
    setShirtData("team_custom", team ? undefined : teamInputValue || undefined);

    // Reset dependent fields when switching to custom team
    if (!team && teamInputValue) {
      setBrandOptions(undefined);
      setPlayerOptions(undefined);
      setNumberOptions(undefined);
      setBrand(undefined);
      setBrandInputValue("");
      setPlayer(undefined);
      setPlayerInputValue("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [team, teamInputValue]);

  useEffect(() => {
    setShirtData("competition_id", competition?.id);
    if (competition?.id) {
      getSeasonsByCompetition(competition.id).then((res) => {
        if (res) {
          const sortedSeasons = res.sort(
            (a, b) => parseInt(b.start_year) - parseInt(a.start_year)
          );
          setSeasonOptions(sortedSeasons);
        }
      });
    } else {
      setSeasonOptions(undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competition]);

  useEffect(() => {
    setShirtData("variant_id", variant?.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [variant]);

  useEffect(() => {
    setShirtData("season_id", season?.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [season]);

  useEffect(() => {
    setShirtData(
      "number",
      number || (numberInputValue ? Number(numberInputValue) : undefined)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [number, numberInputValue]);

  useEffect(() => {
    setShirtData("brand_id", brand?.id);
    setShirtData(
      "brand_custom",
      brand ? undefined : brandInputValue || undefined
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [brand, brandInputValue]);

  useEffect(() => {
    setShirtData("shirt_type_id", shirtType?.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shirtType]);

  useEffect(() => {
    setShirtData("irresistible_offers", openOffers);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openOffers]);

  useEffect(() => {
    // Reset dependent fields when competition or season changes
    setTeam(undefined);
    setTeamInputValue("");
    setPlayer(undefined);
    setPlayerInputValue("");
    setBrand(undefined);
    setBrandInputValue("");
    setNumber(undefined);
    setNumberInputValue("");

    if (competition !== undefined && season !== undefined) {
      getTeamsByCompetitionAndSeason(competition.id, season.id).then(
        (teams) => {
          if (teams) {
            const sortedTeams = teams.sort((a, b) =>
              a.name.localeCompare(b.name)
            );
            setTeamOptions(sortedTeams);
          }
        }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [competition, season]);

  useEffect(() => {
    // Reset dependent fields when team changes
    setPlayer(undefined);
    setPlayerInputValue("");
    setBrand(undefined);
    setBrandInputValue("");
    setNumber(undefined);
    setNumberInputValue("");

    setPlayerOptions(undefined);
    if (
      competition !== undefined &&
      team !== undefined &&
      season !== undefined
    ) {
      getAllPlayersByTeamAndSeasonAndCompetition(
        competition.id,
        season.id,
        team.id
      ).then((players) => {
        if (players && (players?.length ?? 0) > 0) {
          const sortedPlayers = players.sort((a, b) =>
            a.name.localeCompare(b.name)
          );
          setPlayerOptions(sortedPlayers);
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [team, season, competition]);

  useEffect(() => {
    setBrandOptions(undefined);
    if (
      competition !== undefined &&
      team !== undefined &&
      season !== undefined &&
      shirtType !== undefined
    ) {
      getBrandByTeamAndSeason(
        competition.id,
        season.id,
        team.id,
        shirtType.id
      ).then((result) => {
        setBrandOptions(result);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [team, season, competition, shirtType]);

  useEffect(() => {
    // Reset number when player changes
    setNumber(undefined);
    setNumberInputValue("");

    setNumberOptions(undefined);
    if (competition && season && team && player) {
      getNumbersByPlayerInfo(
        competition?.id,
        season?.id,
        team?.id,
        player.id
      ).then((result) => {
        setNumberOptions(result);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [player, team, competition, season]);

  return (
    <Box sx={{ marginTop: "30px" }}>
      <Box marginBottom={"1rem"}>
        <TextField
          required
          label="Item Name"
          variant="outlined"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          fullWidth
          error={errors ? errors["title"] : false}
          sx={tfMainStyle}
          InputLabelProps={tfLabelProps}
          InputProps={{
            sx: tfInputStyle,
          }}
        />
      </Box>

      <Box>
        <TextField
          sx={tfMainStyle}
          InputLabelProps={tfLabelProps}
          InputProps={{
            sx: tfInputStyle,
          }}
          multiline
          rows={4}
          label="Item Description"
          variant="outlined"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          fullWidth
        />
      </Box>

      <Box sx={{ marginTop: "1.5rem", marginBottom: "1rem" }}>
        <Typography
          sx={{
            fontFamily: "Manrope",
            fontSize: "1.4rem !important",
            fontWeight: "700 !important",
            lineHeight: "1.6 !important",
            color: "#3c3c40 !important",
          }}
        >
          Select item categories
        </Typography>
      </Box>

      <Box sx={{ marginBottom: "1rem" }}>
        {typeOptions !== undefined ? (
          <FormControl fullWidth>
            <InputLabel>Shirt Type *</InputLabel>
            <Select
              label="Shirt Type *"
              value={shirtType ? String(shirtType.id) : undefined}
              onChange={(event: SelectChangeEvent) => {
                handleTypeChange(Number(event.target.value));
              }}
              sx={tfMainStyle}
              inputProps={{
                sx: tfInputStyle,
              }}
            >
              {typeOptions.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  <ListItemText>{item.name}</ListItemText>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <Skeleton variant="rectangular" width={"100%"} height={56} />
        )}
      </Box>

      {setProperties && shirtType?.name === "Match" && (
        <ItemPropertyForm
          setProperties={setProperties}
          properties={shirt.properties}
          errors={errors}
        />
      )}

      <Box marginBottom={"1rem"}>
        {conditionOptions !== undefined ? (
          <FormControl fullWidth>
            <InputLabel
              sx={
                errors
                  ? errors["condition_id"] === true
                    ? subcategoryTitleErrorStyle
                    : subcategoryTitleStyle
                  : subcategoryTitleStyle
              }
            >
              Condition *
            </InputLabel>
            <Select
              label="Condition *"
              value={condition ? String(condition.id) : undefined}
              onChange={(event: SelectChangeEvent) => {
                handleConditionChange(Number(event.target.value));
              }}
              sx={tfMainStyle}
              inputProps={{ sx: tfInputStyle }}
            >
              {conditionOptions.map((op) => (
                <MenuItem key={op.id} value={op.id}>
                  <ListItemText>{op.name}</ListItemText>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <Skeleton variant="rectangular" width={"100%"} height={56} />
        )}
      </Box>

      <Box marginBottom={"1rem"}>
        {variantOptions !== undefined ? (
          <FormControl fullWidth>
            <InputLabel>Kit Type *</InputLabel>
            <Select
              label="Kit Type *"
              value={variant ? String(variant.id) : undefined}
              sx={tfMainStyle}
              inputProps={{ sx: tfInputStyle }}
              onChange={(event: SelectChangeEvent) => {
                handleVariantChange(Number(event.target.value));
              }}
            >
              {variantOptions.map((op) => (
                <MenuItem key={op.id} value={op.id}>
                  <ListItemText>{op.name}</ListItemText>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        ) : (
          <Skeleton variant="rectangular" width={"100%"} height={56} />
        )}
      </Box>

      <Box marginBottom={"1rem"}>
        <FormControl fullWidth>
          <InputLabel>Signatures</InputLabel>
          <Select
            label="Signatures"
            value={String(Number(signature))}
            onChange={(event: SelectChangeEvent) => {
              setSignature(Boolean(Number(event.target.value)));
            }}
            sx={tfMainStyle}
            inputProps={{ sx: tfInputStyle }}
          >
            <MenuItem value="0">
              <ListItemText>Not signed</ListItemText>
            </MenuItem>
            <MenuItem value="1">
              <ListItemText>Signed</ListItemText>
            </MenuItem>
          </Select>
        </FormControl>
      </Box>

      {signature && (
        <Box marginBottom={"1rem"}>
          <TextField
            sx={tfMainStyle}
            InputLabelProps={tfLabelProps}
            InputProps={{ sx: tfInputStyle }}
            label="Signature Description"
            value={signatureData}
            onChange={(e) => setSignatureData(e.target.value)}
            variant="outlined"
            multiline
            fullWidth
            rows={4}
          />
        </Box>
      )}

      <Box marginBottom={"1rem"}>
        <FormControl fullWidth>
          <InputLabel>Certification</InputLabel>
          <Select
            label="Certification"
            value={String(Number(certificate))}
            onChange={(event: SelectChangeEvent) => {
              setCertificate(Boolean(Number(event.target.value)));
            }}
            sx={tfMainStyle}
            inputProps={{ sx: tfInputStyle }}
          >
            <MenuItem value="0">
              <ListItemText>Not certified</ListItemText>
            </MenuItem>
            <MenuItem value="1">
              <ListItemText>Certified</ListItemText>
            </MenuItem>
          </Select>
        </FormControl>
      </Box>

      <Box marginBottom={"1rem"}>
        <FormControl fullWidth>
          <InputLabel>Size</InputLabel>
          <Select
            label="Size"
            value={size}
            onChange={(event: SelectChangeEvent) => {
              setSize(event.target.value);
            }}
            sx={tfMainStyle}
            inputProps={{ sx: tfInputStyle }}
          >
            {shirtSizeOptions.map((item, index) => (
              <MenuItem
                value={item}
                key={`shirt-form-size-option-item-${index}`}
              >
                <ListItemText>{item}</ListItemText>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box sx={{ marginBottom: "1rem" }}>
        <Typography
          sx={{
            fontFamily: "Manrope",
            fontSize: "1.4rem !important",
            fontWeight: "700 !important",
            lineHeight: "1.6 !important",
            color: "#3c3c40 !important",
          }}
        >
          Item Details
        </Typography>
        <Typography>
          Please enter item details carefully to ensure precise collection
          Stats.
        </Typography>
      </Box>

      <Box marginBottom={"1rem"}>
        <DatePicker
          label={"Purchase Date"}
          onChange={(newValue) => setPurchaseDate(newValue)}
          value={purchaseDate}
          format="DD/MM/YYYY"
          slotProps={{
            textField: {
              fullWidth: true,
              sx: tfMainStyle,
              InputProps: { sx: tfInputStyle },
              InputLabelProps: tfLabelProps,
              error: errors ? errors["purchase_date"] : false,
            },
          }}
        />
      </Box>

      <Box marginBottom={"1rem"}>
        <TextField
          fullWidth
          required
          label="Purchase Price"
          value={purchasePrice}
          sx={tfMainStyle}
          inputProps={{ sx: tfInputStyle }}
          InputLabelProps={tfLabelProps}
          variant="outlined"
          error={errors ? errors["purchase_price"] : false}
          onChange={(e) => setPurchasePrice(Number(e.target.value))}
        />
      </Box>

      <Box marginBottom={"1rem"}>
        {competitionOptions !== undefined ? (
          <Autocomplete
            options={competitionOptions}
            getOptionLabel={(option) => {
              if (option.country) {
                return `${option.name} - ${option.country}`;
              } else {
                return option.name;
              }
            }}
            value={competition || null}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            onChange={(event, newValue) => {
              if (newValue) {
                handleCompetitionChange(newValue.id);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Competition"
                error={errors ? errors["competition_id"] : false}
                sx={tfMainStyle}
                InputLabelProps={tfLabelProps}
                InputProps={{
                  ...params.InputProps,
                  sx: tfInputStyle,
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.id}>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-start",
                    alignItems: "center",
                  }}
                >
                  {option.logo && (
                    <img
                      src={getUrlLogo(option.logo)}
                      alt={option.name}
                      style={{ width: "20px", height: "auto" }}
                    />
                  )}
                  <Box sx={{ marginLeft: "10px" }}>
                    <ListItemText>
                      {option.name}
                      {option.country && ` - ${option.country}`}
                    </ListItemText>
                  </Box>
                </Box>
              </li>
            )}
          />
        ) : (
          <Skeleton variant="rectangular" width={"100%"} height={56} />
        )}
      </Box>

      <Box marginBottom={"1rem"}>
        <Autocomplete
          options={seasonOptions || []}
          getOptionLabel={(option) => `${option.start_year}/${option.end_year}`}
          value={season || null}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          onChange={(event, newValue) => {
            if (newValue) {
              handleSeasonChange(newValue.id);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Season"
              required
              error={errors ? errors["season_id"] : false}
              sx={tfMainStyle}
              InputLabelProps={tfLabelProps}
              InputProps={{
                ...params.InputProps,
                sx: tfInputStyle,
              }}
            />
          )}
          ListboxProps={{
            style: {
              maxHeight: "300px",
            },
          }}
        />
      </Box>

      <Box marginBottom={"1rem"}>
        <Autocomplete
          freeSolo
          options={teamOptions || []}
          getOptionLabel={(option) =>
            typeof option === "string" ? option : option.name
          }
          value={team || null}
          inputValue={teamInputValue}
          onInputChange={(event, newInputValue) => {
            setTeamInputValue(newInputValue || "");

            if (!newInputValue || newInputValue.trim() === "") {
              setTeam(undefined);
              setBrandOptions(undefined);
              setPlayerOptions(undefined);
              setNumberOptions(undefined);
              setBrand(undefined);
              setBrandInputValue("");
              setPlayer(undefined);
              setPlayerInputValue("");
              return;
            }

            const matchedTeam = teamOptions?.find(
              (t) => t.name.toLowerCase() === newInputValue.toLowerCase()
            );
            if (matchedTeam) {
              setTeam(matchedTeam);
            } else {
              setTeam(undefined);
            }
          }}
          onChange={(event, newValue) => {
            if (newValue && typeof newValue === "object") {
              handleTeamChange(newValue.id);
              setTeamInputValue(newValue.name);
            } else if (typeof newValue === "string") {
              setTeamInputValue(newValue);
              setTeam(undefined);
            }
          }}
          renderInput={(params) => {
            return (
              <TextField
                {...params}
                label="Team"
                required
                error={errors ? errors["team_id"] && !teamInputValue : false}
                sx={tfMainStyle}
                InputLabelProps={tfLabelProps}
                InputProps={{
                  ...params.InputProps,
                  sx: tfInputStyle,
                }}
              />
            );
          }}
          renderOption={(props, option) => (
            <li {...props} key={`teamSelectionItem_${option.id}`}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-start",
                  alignItems: "center",
                }}
              >
                {option.logo && (
                  <img
                    src={getUrlLogo(option.logo)}
                    alt={option.name}
                    style={{ width: "20px", height: "auto" }}
                  />
                )}
                <Box sx={{ marginLeft: "10px" }}>
                  <ListItemText>{option.name}</ListItemText>
                </Box>
              </Box>
            </li>
          )}
          ListboxProps={{
            style: {
              maxHeight: "300px",
            },
          }}
        />
      </Box>

      <Box marginBottom={"1rem"}>
        <Autocomplete
          freeSolo
          options={brandOptions || []}
          getOptionLabel={(option) =>
            typeof option === "string" ? option : option.name
          }
          value={brand || null}
          inputValue={brandInputValue}
          onInputChange={(event, newInputValue) => {
            setBrandInputValue(newInputValue || "");

            if (!newInputValue || newInputValue.trim() === "") {
              setBrand(undefined);
              return;
            }

            const matchedBrand = brandOptions?.find(
              (b) => b.name.toLowerCase() === newInputValue.toLowerCase()
            );
            if (matchedBrand) {
              setBrand(matchedBrand);
            } else {
              setBrand(undefined);
            }
          }}
          onChange={(event, newValue) => {
            if (newValue && typeof newValue === "object") {
              handleBrandChange(newValue.id);
              setBrandInputValue(newValue.name);
            } else if (typeof newValue === "string") {
              setBrandInputValue(newValue);
              setBrand(undefined);
            }
          }}
          renderInput={(params) => {
            return (
              <TextField
                {...params}
                label="Brand"
                required
                error={errors ? errors["brand_id"] && !brandInputValue : false}
                sx={tfMainStyle}
                InputLabelProps={tfLabelProps}
                InputProps={{
                  ...params.InputProps,
                  sx: tfInputStyle,
                }}
              />
            );
          }}
          ListboxProps={{
            style: {
              maxHeight: "300px",
            },
          }}
        />
      </Box>

      <Box marginBottom={"1rem"}>
        <Autocomplete
          freeSolo
          options={playerOptions || []}
          getOptionLabel={(option) =>
            typeof option === "string" ? option : getPlayerName(option)
          }
          value={player || null}
          inputValue={playerInputValue}
          onInputChange={(event, newInputValue) => {
            setPlayerInputValue(newInputValue || "");

            if (!newInputValue || newInputValue.trim() === "") {
              setPlayer(undefined);
              setNumberOptions(undefined);
              return;
            }

            const matchedPlayer = playerOptions?.find(
              (p) =>
                getPlayerName(p).toLowerCase() === newInputValue.toLowerCase()
            );
            if (matchedPlayer) {
              setPlayer(matchedPlayer);
            } else {
              setPlayer(undefined);
              setNumberOptions(undefined);
            }
          }}
          onChange={(event, newValue) => {
            if (newValue && typeof newValue === "object") {
              handlePlayerChange(newValue.id);
              setPlayerInputValue(getPlayerName(newValue));
            } else if (typeof newValue === "string") {
              setPlayerInputValue(newValue);
              setPlayer(undefined);
            }
          }}
          renderInput={(params) => {
            return (
              <TextField
                {...params}
                label="Player"
                sx={tfMainStyle}
                InputLabelProps={tfLabelProps}
                InputProps={{
                  ...params.InputProps,
                  sx: tfInputStyle,
                }}
              />
            );
          }}
          ListboxProps={{
            style: {
              maxHeight: "300px",
            },
          }}
        />
      </Box>

      <Box marginBottom={"1rem"}>
        <Autocomplete
          freeSolo
          options={numberOptions || []}
          getOptionLabel={(option) => String(option)}
          value={number || null}
          inputValue={numberInputValue}
          onInputChange={(event, newInputValue) => {
            setNumberInputValue(newInputValue || "");
            const numberValue = newInputValue ? parseInt(newInputValue) : NaN;
            if (!isNaN(numberValue) && numberOptions?.includes(numberValue)) {
              setNumber(numberValue);
            } else {
              setNumber(undefined);
            }
          }}
          onChange={(event, newValue) => {
            if (typeof newValue === "number") {
              setNumber(newValue);
              setNumberInputValue(String(newValue));
            } else if (typeof newValue === "string" && newValue.trim()) {
              setNumberInputValue(newValue);
              const numberValue = parseInt(newValue);
              if (!isNaN(numberValue)) {
                setNumber(numberValue);
              } else {
                setNumber(undefined);
              }
            } else if (newValue === null || newValue === "") {
              setNumber(undefined);
              setNumberInputValue("");
            }
          }}
          renderInput={(params) => {
            const safeParams = params || {};

            return (
              <TextField
                {...safeParams}
                label="Number"
                sx={tfMainStyle}
                InputLabelProps={tfLabelProps}
                InputProps={{
                  ...(safeParams.InputProps || {}),
                  sx: tfInputStyle,
                }}
                inputProps={{
                  ...(safeParams.inputProps || {}),
                  min: 0,
                  max: 99,
                }}
              />
            );
          }}
          ListboxProps={{
            style: {
              maxHeight: "300px",
            },
          }}
        />
      </Box>

      <Box
        sx={{
          display: { xs: "block", md: "flex" },
          justifyContent: "space-between",
          marginTop: "20px",
        }}
      >
        <Box>
          <Typography>
            Current Price
            <IconButton
              onClick={handleClick}
              sx={{ padding: 0, marginLeft: "10px" }}
            >
              <HelpOutline />
            </IconButton>
            <Popover
              id={id}
              open={open}
              anchorEl={anchorEl}
              onClose={handleClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "left",
              }}
            >
              <Box sx={{ width: "300px", padding: "0.8rem 0.7rem" }}>
                Our price predictor currently estimates the value of shirts by
                analyzing and comparing them against the prices of thousands of
                shirts sold over the past year. It considers factors such as the
                player, team, and specific shirt details (e.g., whether it is
                match-worn). At this stage, we rely on historical sales data and
                mathematical modeling to calculate a shirt's value.
                <br />
                <br />
                Meanwhile, we're training an AI model behind the scenes to
                improve prediction accuracy. Once we've collected enough data,
                the AI will fully take over the valuation process. If you
                believe a shirt's estimated value is incorrect, please enter the
                amount you think is right in the field on the right, and
                overwrite our suggested value by clicking{" "}
                <strong>SAVE & OVERWRITE</strong>.
                <br />
                <br />
                <strong>Note:</strong> BASIC Collectors can view the purchasing
                value, the actual value and virtual profit only for their first
                10 uploaded shirts. VIP Collectors unlock these values for all
                shirts in their collection.
              </Box>
            </Popover>
          </Typography>
          <Typography
            variant="statNumber"
            sx={
              getMoneyFormat(shirt?.current_price).length > 9
                ? { fontSize: "1.5rem !important", fontStyle: "italic" }
                : { fontSize: "2rem !important", fontStyle: "italic" }
            }
          >
            {getMoneyFormat(shirt?.current_price)}
          </Typography>
        </Box>
        <Box>
          <Typography sx={{ marginBottom: "2px" }}>
            Disagree? Give your own value.
          </Typography>
          <Box display="flex" alignItems="center">
            <TextField
              placeholder="Amount"
              value={customPrice}
              onChange={(event) => setCustomPrice(event.target.value)}
              variant="outlined"
              size="small"
              sx={{ ...tfMainStyle, width: "100px", marginRight: "10px" }}
              InputLabelProps={tfLabelProps}
              InputProps={{
                sx: { ...tfInputStyle, paddingY: "3px" },
              }}
            />
            <Button
              variant="blue"
              sx={{
                fontSize: "16px !important",
                fontWeight: 500,
                paddingY: "8px !important",
                fontStyle: "italic",
                lineHeight: "28px !important",
              }}
              className="default-btn"
              onClick={() => {
                if (customPrice && shirt?.id) {
                  updateCustomPrice(shirt.id, customPrice);
                  closeDialog();
                }
              }}
            >
              Save & Overwrite
            </Button>
          </Box>
        </Box>
      </Box>

      <Box sx={{ padding: "1rem 0rem" }}>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            flexDirection: "row",
            alignItems: "center",
            margin: "1rem 0rem",
            columnGap: 2,
          }}
        >
          <Box>
            <Typography variant="body1" sx={{ fontSize: "1rem !important" }}>
              Open to irresistible offers?
            </Typography>
          </Box>
          <Box>
            <PremiumWrapperButton
              action={Actions.UPDATE_OPEN_OFFERS}
              entity={Subjects.SHIRT}
              text="Upgrade"
              size="200px"
              component={
                <CustomSwitch
                  sx={{ marginLeft: "14px" }}
                  checked={openOffers}
                  onChange={(e) => setOpenOffers(e.target.checked)}
                  inputProps={{ "aria-label": "controlled" }}
                />
              }
            />
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            justifyContent: "space-between",
            flexDirection: "row",
            alignItems: "center",
            columnGap: 2,
          }}
        >
          <Box>
            <Typography variant="body1" sx={{ fontSize: "1rem !important" }}>
              Make shirt private?
            </Typography>
          </Box>
          <Box>
            <PremiumWrapperButton
              action={Actions.UPDATE_VISIBILITY}
              entity={Subjects.SHIRT}
              text="Upgrade"
              size="200px"
              component={
                <CustomSwitch
                  sx={{ marginLeft: "14px" }}
                  checked={isPrivate}
                  onChange={(e) => setIsPrivate(e.target.checked)}
                  inputProps={{ "aria-label": "controlled" }}
                />
              }
            />
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default EditShirtForm;
